import * as Sentry from "@sentry/react";

import { ProviderOption } from "lib/generated/integration-api/models/ProviderOption";
import { ProviderTypes } from "lib/generated/integration-api/models/ProviderTypes";
import UIcon, { CustomIconKey } from "lib/icons/untitled_ui/UIcon";
import { dynamicsBlue, hubspotOrange, salesforceBlue, zohoBlue } from "lib/themes/colors";
import { ContactSource } from "lib/types/graphQLEnums";
import {
  isParagonOnlyProvider,
  NativeProviderType,
  PROVIDER_METADATA,
  PROVIDER_OPPORTUNITY_METADATA,
  ProviderMetadata,
  ProviderOpportunityMetadata,
} from "./providerMetadata";

/** A provider option this file has copy and icons for. */
export type NativeProviderOption = ProviderOption & { type: NativeProviderType };

/**
 * Narrowing form of the Paragon-only check, so filtering a provider list leaves options that can be
 * handed to the provider-keyed maps without each call site re-proving it.
 */
export function isNativeProvider(provider: ProviderOption): provider is NativeProviderOption {
  return !isParagonOnlyProvider(provider.type);
}

export const providerIconMap: Record<ProviderTypes, CustomIconKey> = {
  SALESFORCE: "salesforce",
  HUBSPOT: "hubspot",
  DYNAMICS: "dynamics",
  ZOHO: "zoho",
};

export function getProviderIconColor(providerType: ProviderTypes) {
  return providerType === ProviderTypes.Hubspot ? "#ff7a59" : "#00A1E0";
}

export function getIntegrationIcon(providerType: ProviderTypes, size: number = 16): JSX.Element {
  switch (providerType) {
    case ProviderTypes.Hubspot:
      return <UIcon icon="hubspot" color={hubspotOrange} size={size} />;
    case ProviderTypes.Salesforce:
      return <UIcon icon="salesforce" color={salesforceBlue} size={size} />;
    case ProviderTypes.Dynamics:
      return <UIcon icon="dynamics" color={dynamicsBlue} size={size} />;
    case ProviderTypes.Zoho:
      return <UIcon icon="zoho" color={zohoBlue} size={size} />;
    default:
      throw new Error(`Unsupported integration ${providerType}.`);
  }
}

export function getProviderMetadata(
  providerType: NativeProviderType,
): ProviderMetadata | ProviderOpportunityMetadata {
  return PROVIDER_OPPORTUNITY_METADATA[providerType];
}

export const friendlyProviderName: Record<ProviderTypes, string> = {
  [ProviderTypes.Hubspot]: "HubSpot",
  [ProviderTypes.Salesforce]: "Salesforce",
  [ProviderTypes.Dynamics]: "Microsoft Dynamics",
  [ProviderTypes.Zoho]: "Zoho CRM",
};

export function getNoticeHeaderCopy(providerType: NativeProviderType, entityName: string) {
  const headerCopy = PROVIDER_METADATA[providerType].createNoticeModal[entityName];
  if (!headerCopy) throw `Unsupported entity name ${entityName} for provider ${providerType}`;
  else return headerCopy;
}

export function getOpportunityHeaderCopy(providerType: NativeProviderType, entityName: string) {
  const headerCopy = PROVIDER_OPPORTUNITY_METADATA[providerType].createOpportunityModal[entityName];
  if (!headerCopy) throw `Unsupported entity name ${entityName} for provider ${providerType}`;
  else return headerCopy;
}

/**
 * The stable Cognism contact id to send to the integrations service, which derives the CRM object
 * mapping id from it. Oscar contacts have none, so the service keys off the contact's own id.
 * A Cognism contact reaching a CRM push always carries cognismContactId (set by the lead + search
 * read paths); if it somehow doesn't, surface it — the fallback id isn't stable across views and
 * would create duplicate CRM records.
 */
export function crmCognismContactId(contact: {
  source?: ContactSource | null;
  cognismContactId?: string | null;
}): string | undefined {
  if (contact.source === ContactSource.Cognism && !contact.cognismContactId) {
    Sentry.captureMessage(
      "crmCognismContactId: Cognism contact missing cognismContactId",
      "warning",
    );
  }
  return contact.cognismContactId ?? undefined;
}
